import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  FIRST_PAGE_ITEMS_LIMIT,
  ITEMS_PER_PAGE_LIMIT,
  SELECTION_VIEW_MORE_TYPES,
  SELECTION_WIDGET_TYPES,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useContentItems } from "@is/hooks";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  ContentItemType,
  ProductItem,
  WidgetItem as WidgetItemType,
  WidgetType,
} from "@is/types";

type Props = {
  widget: WidgetItemType;
  sectionId: string;
  contentItemId?: string;
  widgetType?: WidgetType;
  maxPageItemsLimit?: number;
};

const PAGE_DIFFERENCE_ANIMATION_DELAY = 2;
const ANIMATION_CLOSE_WIDGET = 500;

const useManageContentWidgetItem = ({
  widget,
  contentItemId,
  widgetType,
  sectionId,
  maxPageItemsLimit,
}: Props) => {
  const { t } = useTranslation(TranslationContext.ProductListing);

  const { quickAddToBasketItem, sections, updateViewState } =
    useContext(ViewStateContext).content;

  const { handleOpenProductDetails } = useContentItems();

  const [page, setPage] = useState(1);

  const widgetRef = useRef(null);

  const firstPageItemsLimit = useMemo(
    () =>
      widgetType === WidgetType.AllColors
        ? maxPageItemsLimit
        : FIRST_PAGE_ITEMS_LIMIT,
    [maxPageItemsLimit, widgetType]
  );

  const itemsPerPageLimit = useMemo(
    () =>
      widgetType === WidgetType.AllColors
        ? maxPageItemsLimit
        : ITEMS_PER_PAGE_LIMIT,
    [maxPageItemsLimit, widgetType]
  );

  const items = useMemo(() => {
    if (page === 1) {
      return widget.items.slice(0, firstPageItemsLimit);
    }

    const totalItems = firstPageItemsLimit + (page - 1) * itemsPerPageLimit;

    if (widget.items.length <= totalItems) {
      return widget.items;
    }

    return widget.items.slice(0, totalItems);
  }, [firstPageItemsLimit, itemsPerPageLimit, page, widget]);

  const showViewMoreButton = useMemo(
    () => widget.items.length > items.length,
    [widget, items]
  );

  const noMoreItemsToLoad = useMemo(
    () =>
      widget.items.length <= items.length && items.length > firstPageItemsLimit,
    [firstPageItemsLimit, items, widget]
  );

  const widgetTitle = useMemo(() => {
    switch (widget.type) {
      case WidgetType.AllColors:
        return t("All Colors");
      case WidgetType.AllItems:
        return t("All Items");
      default:
        return widget.title;
    }
  }, [t, widget]);

  const getAnimationDelayIndex = useCallback(
    (itemIndex: number) => {
      let newValue = itemIndex;

      if (page === 1) {
        return newValue;
      }

      if (page > 1) {
        newValue =
          itemIndex -
          (firstPageItemsLimit +
            (page - PAGE_DIFFERENCE_ANIMATION_DELAY) * itemsPerPageLimit);
      }

      return newValue < 0 ? 0 : newValue;
    },
    [firstPageItemsLimit, itemsPerPageLimit, page]
  );

  const handleViewMoreClick = useCallback(() => {
    trackEvent({
      eventType: SELECTION_VIEW_MORE_TYPES[widgetType],
      data: {
        ...(widgetType === WidgetType.Collection && {
          collectionName: widget.title,
        }),
      },
    });

    setPage((prev) => prev + 1);
  }, [widget, widgetType]);

  const handleItemClick = useCallback(
    (productItem: ProductItem) => {
      trackEvent({
        eventType: SELECTION_WIDGET_TYPES[widgetType],
        data: {
          variantId: productItem.id,
          styleId: productItem.styleId,
          ...(widgetType === WidgetType.Collection && {
            collectionName: widget.title,
          }),
        },
      });

      handleOpenProductDetails({
        productItem,
        type: ContentItemType.WidgetItem,
        contentItemId,
        parentItemId: contentItemId,
      });
    },
    [widgetType, widget, handleOpenProductDetails, contentItemId]
  );

  const handleQuickAddToBasket = useCallback(
    (item: ProductItem) => {
      trackEvent({
        eventType: TrackingEventTypes.QuickAddToBasketOpen,
        data: { id: item.id, styleId: item.styleId },
      });

      updateViewState({ quickAddToBasketItem: item });
    },
    [updateViewState]
  );

  const handleQuickAddToBasketRemoveItem = useCallback(() => {
    updateViewState({ quickAddToBasketItem: null });
  }, [updateViewState]);

  const handleQuickAddToBasketLayerClick = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const handleCloseWidget = useCallback(() => {
    const sectionsWithClosedWidget = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.map((item) => {
            if (
              item.type === ContentItemType.ContentWidgetItem &&
              (item.content as WidgetItemType).id === widget.id
            ) {
              return {
                ...item,
                content: {
                  ...item.content,
                  isClosed: true,
                },
              };
            }

            return item;
          }),
        };
      }

      return section;
    });

    const sectionsWithoutWidget = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.filter((item) => {
            if (item.type === ContentItemType.ContentWidgetItem) {
              const content = item.content as WidgetItemType;
              return content.id !== widget.id;
            }

            return true;
          }),
        };
      }

      return section;
    });

    updateViewState({ sections: sectionsWithClosedWidget });

    setTimeout(() => {
      updateViewState({ sections: sectionsWithoutWidget });
    }, ANIMATION_CLOSE_WIDGET);
  }, [sectionId, sections, updateViewState, widget.id]);

  const notOnlyItemInSection = useMemo(
    () =>
      sections.find((section) => section.id === sectionId)?.items.length > 1,
    [sectionId, sections]
  );

  useEffect(() => {
    if (widgetRef?.current) {
      setTimeout(() => {
        widgetRef.current.scrollIntoView({
          behavior: "smooth",
          inline: "nearest",
          block: "nearest",
        });
      }, 300);
    }
  }, [widget]);

  return {
    items,
    showViewMoreButton,
    handleViewMoreClick,
    handleItemClick,
    widgetRef,
    getAnimationDelayIndex,
    t,
    noMoreItemsToLoad,
    quickAddToBasketItem,
    handleQuickAddToBasket,
    handleQuickAddToBasketRemoveItem,
    handleQuickAddToBasketLayerClick,
    handleCloseWidget,
    notOnlyItemInSection,
    widgetTitle,
  };
};

export default useManageContentWidgetItem;
